import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useStringsContext } from '../../../contexts/strings';
import { ROLES, SxStyle } from '../../../types';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import firebaseApp from '../../../firebase-app';
import Paper from '@mui/material/Paper';
import { query, limit, where, setDoc, DocumentReference, updateDoc } from 'firebase/firestore';
import { collection, getDocs } from 'firebase/firestore';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { FS_PATH_STORIES } from '../../../constants';
import moment from 'moment';
import svStrings from '../../../sv-strings';
import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Story, StoryStatus, VideoData } from './types';
import { convertFirestoreStoryToDatagridFormat, StoryDataGridFormat } from './data';
import StoryDeleteModal from './components/modals/story-delete-modal';
import StoryEditModal from './components/modals/story-edit-modal';
import StoryListHeader from './components/story-list-header';
import ListDateAndImage from './components/story-list-date-image';
import ListToggle from './components/story-list-toggle';
import { useDocumentData } from 'react-firebase-hooks/firestore';
import { getAppConfigPath, getStoryPath } from '../../../utils/paths';
import { doc } from 'firebase/firestore';
import { useFotbollPlayVideo } from './hooks/video';
import { useSnackbar } from 'notistack';
import { AppConfigData } from '../../../types/app-config';
import ScenePage from '../../common/scene/scene-page';
import StoryPushNotificationColumn from './components/story-list-push-notification';
import { useUserAuth } from '../../../contexts/auth';

const styles: SxStyle = {
  container: (theme) => ({
    width: '100%',
    height: 'calc(100% - 80px)',
    marginTop: theme.spacing(4),
  }),
  dataGrid: (theme) => ({
    '& .MuiDataGrid-root .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer, .MuiDataGrid-columnHeaderTitleContainerContent': {
      width: '100%',
    },
    '& .MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: theme.palette.shades.p10,
    },
  }),
};

export const getStatus = (story: Story): StoryStatus => {
  const start = moment(story.startTime);
  const now = moment();

  if (story.hidden) {
    return 'HIDDEN';
  }

  if (now.isBefore(start)) {
    return 'SCHEDULED';
  }

  return 'PUBLISHED';
};

const ListStatusHeader = () => (
  <Stack direction="row" alignItems="center" spacing={2}>
    <Box sx={{ fontWeight: 500 }}>{svStrings.polls.fieldNameStatus}</Box>
    <Tooltip
      placement="top"
      arrow
      title={
        <>
          <div>{svStrings.stories.toolTipPublished}</div>
          <div>{svStrings.stories.toolTipScheduled}</div>
        </>
      }
    >
      <InfoIcon fontSize="small" color="action" />
    </Tooltip>
  </Stack>
);

interface ListTypeProps {
  params: GridRenderCellParams;
}

const ListType = ({ params }: ListTypeProps) => {
  return (
    <div>
      {params.row.type === 'VIDEO' ? svStrings.stories.typeVideo : svStrings.stories.typeImage}
    </div>
  );
};

interface ListStatusProps {
  params: GridRenderCellParams;
}

const ListStatus = ({ params }: ListStatusProps) => {
  const status = getStatus(params.row.story);
  let text = status as string;
  let color;

  if (status === 'PUBLISHED') {
    text = svStrings.stories.statusPublished;
    color = 'success.main';
  }
  if (status === 'SCHEDULED') {
    text = svStrings.stories.statusScheduled;
    color = 'warning.main';
  }
  if (status === 'HIDDEN') {
    text = svStrings.stories.statusHidden;
    color = 'error.main';
  }

  return (
    <Typography variant="body2" color={color}>
      {text}
    </Typography>
  );
};

interface ListEditProps {
  params: GridRenderCellParams;
  onEdit: (event: React.MouseEvent, poll: StoryDataGridFormat) => void;
  onDelete: (event: React.MouseEvent, poll: StoryDataGridFormat) => void;
  isReadOnly: boolean;
}

const ListEdit = ({ params, onEdit, onDelete, isReadOnly }: ListEditProps) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <IconButton onClick={(event) => onEdit(event, params.row)} disabled={isReadOnly}>
      <EditIcon color={isReadOnly ? 'disabled' : 'primary'} />
    </IconButton>
    <IconButton onClick={(event) => onDelete(event, params.row)} disabled={isReadOnly}>
      <DeleteIcon color={isReadOnly ? 'disabled' : 'error'} />
    </IconButton>
  </Stack>
);

interface Props {
  match: any;
}

export default function StoriesList({ match }: Props) {
  const { strings } = useStringsContext();
  const { fetchVideoData } = useFotbollPlayVideo();
  const { enqueueSnackbar } = useSnackbar();

  const [loadingStories, setLoadingStories] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedStory, setSelectedStory] = useState<Story | null>(null);
  const [storiesRows, setStoriesRows] = useState<StoryDataGridFormat[] | null>(null);

  const { abbrv: selectedAbbrv } = match.params;

  const { isSuperAdmin, hasTeamRole } = useUserAuth();
  const isReadOnly = !isSuperAdmin && hasTeamRole(selectedAbbrv, ROLES.VIEWER);

  const [appConfig, loadingAppConfig] = useDocumentData<AppConfigData>(
    doc(firebaseApp.db, getAppConfigPath(selectedAbbrv)) as DocumentReference<AppConfigData>
  );

  const selectedLeague = appConfig?.configLeagueName;

  const updateThumbnailsFromVideo = useCallback(
    async (storiesRows: StoryDataGridFormat[]) => {
      if (selectedLeague) {
        for (const storyRow of storiesRows) {
          try {
            const videoId = storyRow.story.videoUrl.split('/').pop();
            if (videoId) {
              const videoData: VideoData = await fetchVideoData(videoId, selectedLeague);
              storyRow.thumbnailUrl = videoData.thumbnail_url;
            }
          } catch (error) {
            console.error(error);
          }
        }
      }
    },
    [fetchVideoData, selectedLeague]
  );

  const getStories = useCallback(async () => {
    try {
      const colRef = collection(firebaseApp.db, FS_PATH_STORIES);
      const docs = await getDocs(query(colRef, where('abbrv', '==', selectedAbbrv), limit(25)));
      const newStories = docs?.docs?.map((doc) => doc.data()) as Story[];

      const storiesRowsNew = newStories.map(convertFirestoreStoryToDatagridFormat);
      await updateThumbnailsFromVideo(storiesRowsNew);

      setStoriesRows(storiesRowsNew);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(strings.messages.loadingError, {
        variant: 'error',
      });
    }
    setLoadingStories(false);
  }, [enqueueSnackbar, selectedAbbrv, strings, updateThumbnailsFromVideo]);

  const onEdit = useCallback((event: React.MouseEvent, storyRow: StoryDataGridFormat) => {
    event.stopPropagation();
    setSelectedStory(storyRow.story);
    setShowEditDialog(true);
  }, []);

  const onDelete = useCallback((event: React.MouseEvent, storyRow: StoryDataGridFormat) => {
    event.stopPropagation();
    setSelectedStory(storyRow.story);
    setShowDeleteDialog(true);
  }, []);

  const onDeleteEdit = useCallback(() => {
    setShowDeleteDialog(true);
  }, []);

  const onAdd = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedStory(null);
    setShowEditDialog(true);
  }, []);

  const onCloseEdit = useCallback(() => {
    setShowEditDialog(false);
    setSelectedStory(null);
  }, []);

  const onCloseDelete = useCallback(() => {
    setShowDeleteDialog(false);
  }, []);

  const onEditDone = useCallback(() => {
    setShowEditDialog(false);
    setShowDeleteDialog(false);
    setSelectedStory(null);
    getStories();
  }, [getStories]);

  const onDoneDelete = useCallback(() => {
    setShowEditDialog(false);
    setShowDeleteDialog(false);
    setSelectedStory(null);
    getStories();
  }, [getStories]);

  const onShownChanged = useCallback(
    async (storyId: string, checked: boolean) => {
      try {
        await setDoc(
          doc(firebaseApp.db, getStoryPath(storyId)),
          {
            hidden: !checked,
          },
          { merge: true }
        );

        const message = checked
          ? strings.messages.storyShowSuccess
          : strings.messages.storyHiddenSuccess;

        await getStories();
        enqueueSnackbar(message, {
          variant: 'success',
        });
      } catch (error: any) {
        console.error(error);

        enqueueSnackbar(strings.messages.saveError, {
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, getStories, strings]
  );

  const onReactionsChanged = useCallback(
    async (storyId: string, checked: boolean) => {
      try {
        await setDoc(
          doc(firebaseApp.db, getStoryPath(storyId)),
          {
            disableReactions: !checked,
          },
          { merge: true }
        );

        const message = checked
          ? strings.messages.reactionsEnabled
          : strings.messages.reactionsDisabled;

        await getStories();
        enqueueSnackbar(message, {
          variant: 'success',
        });
      } catch (error: any) {
        console.error(error);

        enqueueSnackbar(strings.messages.saveError, {
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, getStories, strings]
  );

  const onCommentsChanged = useCallback(
    async (storyId: string, checked: boolean) => {
      try {
        await setDoc(
          doc(firebaseApp.db, getStoryPath(storyId)),
          {
            disableComments: !checked,
          },
          { merge: true }
        );

        const message = checked
          ? strings.messages.commentsEnabled
          : strings.messages.commentsDisabled;

        await getStories();
        enqueueSnackbar(message, {
          variant: 'success',
        });
      } catch (error: any) {
        console.error(error);

        enqueueSnackbar(strings.messages.saveError, {
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, getStories, strings]
  );

  const onFeatureFlagChanged = useCallback(
    async (flag: string, value: boolean) => {
      if (!appConfig || !selectedAbbrv) {
        return;
      }

      try {
        const docRef = doc(firebaseApp.db, getAppConfigPath(selectedAbbrv));
        await updateDoc(docRef, {
          featureFlags: {
            ...(appConfig.featureFlags ? appConfig.featureFlags : {}),
            [flag]: value,
          },
        });

        enqueueSnackbar(strings.messages.saveSuccess, {
          variant: 'success',
        });
      } catch (error) {
        console.error(error);

        enqueueSnackbar(strings.messages.saveError, {
          variant: 'error',
        });
      }
    },
    [appConfig, enqueueSnackbar, selectedAbbrv, strings]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'createdAt',
        headerName: strings.stories.fieldNameCreated,
        width: 200,
        renderCell: (params: GridRenderCellParams) => <ListDateAndImage params={params} />,
      },
      { field: 'caption', headerName: strings.stories.fieldNameCaption, width: 160 },
      {
        field: 'type',
        headerName: strings.stories.fieldNameType,
        width: 100,
        renderCell: (params: GridRenderCellParams) => <ListType params={params} />,
      },
      { field: 'views', headerName: strings.stories.fieldNameViews, width: 100 },
      {
        field: 'status',
        headerName: strings.stories.fieldNameStatus,
        minWidth: 100,
        flex: 1,
        // sortComparator: (a, b) => sortOrderStatus.indexOf(a) - sortOrderStatus.indexOf(b),
        renderHeader: () => <ListStatusHeader />,
        renderCell: (params: GridRenderCellParams) => <ListStatus params={params} />,
      },
      {
        field: 'shown',
        headerName: strings.stories.fieldNameShown,
        width: 100,
        renderCell: (params: GridRenderCellParams) => (
          <ListToggle
            value={params.row.shown}
            onChange={(_, checked: boolean) => onShownChanged(params.row.id, checked)}
            isReadOnly={isReadOnly}
          />
        ),
        align: 'center',
      },
      {
        field: 'reactions',
        headerName: strings.stories.fieldNameReactions,
        width: 100,
        renderCell: (params: GridRenderCellParams) => (
          <ListToggle
            value={params.row.reactions}
            onChange={(_, checked: boolean) => onReactionsChanged(params.row.id, checked)}
            isReadOnly={isReadOnly}
          />
        ),
        align: 'center',
      },
      {
        field: 'comments',
        headerName: strings.stories.fieldNameComments,
        width: 100,
        renderCell: (params: GridRenderCellParams) => (
          <ListToggle
            value={params.row.comments}
            onChange={(_, checked: boolean) => onCommentsChanged(params.row.id, checked)}
            isReadOnly={isReadOnly}
          />
        ),
        align: 'center',
      },
      {
        field: 'push-notification',
        headerName: strings.stories.fieldNamePushNotification,
        width: 300,
        renderCell: (params: GridRenderCellParams) => (
          <StoryPushNotificationColumn story={params.row} selectedAbbrv={selectedAbbrv} />
        ),
        align: 'center',
      },
      {
        field: 'edit',
        headerName: '',
        minWidth: 100,
        maxWidth: 100,
        align: 'right',
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <ListEdit params={params} onEdit={onEdit} onDelete={onDelete} isReadOnly={isReadOnly} />
        ),
      },
    ],
    [
      isReadOnly,
      strings,
      onShownChanged,
      onReactionsChanged,
      onCommentsChanged,
      selectedAbbrv,
      onEdit,
      onDelete,
    ]
  );

  useEffect(() => {
    if (selectedAbbrv && selectedLeague) {
      getStories();
    }
  }, [getStories, selectedAbbrv, selectedLeague]);

  const isLoading = loadingStories || loadingAppConfig;
  const reactionsEnabled =
    appConfig?.featureFlags?.disableReactionsStories === undefined
      ? true
      : !appConfig.featureFlags.disableReactionsStories;

  return (
    <>
      <ScenePage title={strings.appBar.stories}>
        <StoryListHeader
          onAdd={onAdd}
          reactionsEnabled={reactionsEnabled}
          onReactionsChanged={(enabled) =>
            onFeatureFlagChanged('disableReactionsStories', !enabled)
          }
          isReadOnly={isReadOnly}
        />
        <Paper elevation={2} sx={styles.container}>
          <DataGrid
            key={selectedAbbrv}
            sx={styles.dataGrid}
            rows={storiesRows || []}
            columns={columns}
            autoPageSize
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            loading={isLoading}
            initialState={{
              sorting: {
                sortModel: [
                  {
                    field: 'createdAt',
                    sort: 'desc',
                  },
                ],
              },
            }}
          />
        </Paper>
      </ScenePage>
      {showEditDialog && selectedAbbrv && selectedLeague && (
        <StoryEditModal
          open={showEditDialog}
          onClose={onCloseEdit}
          onEditDone={onEditDone}
          onDelete={onDeleteEdit}
          selectedStory={selectedStory}
          selectedAbbrv={selectedAbbrv}
          selectedLeague={selectedLeague}
        />
      )}
      {showDeleteDialog && selectedStory && (
        <StoryDeleteModal
          open={showDeleteDialog}
          onClose={onCloseDelete}
          onDone={onDoneDelete}
          selectedStory={selectedStory}
        />
      )}
    </>
  );
}
